/**
 * Espelho de `backend/src/types/documentAttachment.ts` (#520). No catálogo o id é texto (vai para a
 * rota); na emissão é número (volta em `attachmentIds`, que o servidor só aceita inteiro).
 */
import type { ReportType } from './occupationalRisk';

export const TIPOS_COM_ANEXO = ['pcmso', 'pgr'] as const;

export type DocumentAttachmentType = (typeof TIPOS_COM_ANEXO)[number];

export const ROTULO_DO_DOCUMENTO: Record<DocumentAttachmentType, string> = {
  pcmso: 'PCMSO',
  pgr: 'PGR',
};

export function aceitaAnexo(type: ReportType | ''): type is DocumentAttachmentType {
  return (TIPOS_COM_ANEXO as readonly string[]).includes(type);
}

export interface DocumentAttachment {
  id: string;
  documentType: DocumentAttachmentType;
  numeral: string | null;
  title: string;
  normReference: string | null;
  content: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentAttachmentPayload {
  documentType: DocumentAttachmentType;
  numeral: string | null;
  title: string;
  normReference: string | null;
  content: string;
  displayOrder: number;
  isActive: boolean;
}

export interface AttachmentOption {
  id: number;
  numeral: string | null;
  title: string;
  normReference: string | null;
}

export type AttachmentSnapshotItem = AttachmentOption;

/** Anexo montado pelo sistema com os dados da empresa, que quem emite pode deixar de fora (#551). */
export interface AnnexBlockOption {
  key: string;
  numeral: string;
  title: string;
}

export interface AttachmentOptionsResult {
  attachments: AttachmentOption[];
  lastSelection: number[];
  /** Ausente em servidor anterior à #551: vale como nenhum. */
  annexBlocks?: AnnexBlockOption[];
  /** Chaves que a última emissão desta empresa deixou de fora. */
  lastOmittedAnnexBlocks?: string[];
}

/** "ANEXO II – Audiometria", ou "ANEXO – Audiometria" enquanto o número não é definido. */
export function rotuloDoAnexo(anexo: Pick<AttachmentOption, 'numeral' | 'title'>): string {
  const numero = anexo.numeral?.trim() ?? '';
  return numero === '' ? `ANEXO – ${anexo.title}` : `ANEXO ${numero} – ${anexo.title}`;
}

const ALGARISMO_ROMANO = /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
const VALOR_DO_SIMBOLO: Readonly<Record<string, number>> = {
  I: 1,
  V: 5,
  X: 10,
  L: 50,
  C: 100,
  D: 500,
  M: 1000,
};

/** O valor de um número de anexo escrito em algarismo romano; `null` quando ele não é romano. */
export function lerAlgarismoRomano(numeral: string | null): number | null {
  const texto = numeral?.trim().toUpperCase() ?? '';
  if (texto === '' || !ALGARISMO_ROMANO.test(texto)) return null;

  const valores = [...texto].map((simbolo) => VALOR_DO_SIMBOLO[simbolo] ?? 0);
  return valores.reduce(
    (total, valor, indice) => total + (valor < (valores[indice + 1] ?? 0) ? -valor : valor),
    0,
  );
}

/**
 * Espelho de `intercalarAnexos` do backend: encaixa cada anexo montado antes do primeiro item da
 * lista cujo número, lido como romano, é maior que o dele; nenhum maior, vai para o fim. A tela
 * mostra os anexos na mesma ordem em que saem no papel.
 */
export function intercalarAnexos<C extends { numeral: string | null }, Q extends { numeral: string }>(
  catalogo: readonly C[],
  quadros: readonly Q[],
): Array<C | Q> {
  const lista: Array<C | Q> = [...catalogo];

  for (const quadro of quadros) {
    const valor = lerAlgarismoRomano(quadro.numeral);
    const posicao =
      valor === null
        ? -1
        : lista.findIndex((item) => {
            const doItem = lerAlgarismoRomano(item.numeral);
            return doItem !== null && doItem > valor;
          });
    lista.splice(posicao === -1 ? lista.length : posicao, 0, quadro);
  }

  return lista;
}
